import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import pb from '@/lib/pocketbase'
import type { UserRole } from '@/types/auth'
import type { ExtendedUser } from '@/types/auth'

interface AuthStore {
  user: ExtendedUser | null
  role: UserRole
  isAuthenticated: boolean
  assignedKegiatanIds: string[]
  adminCollection: string
  login: (noWa: string, password: string) => Promise<void>
  logout: () => void
  setAssignedKegiatan: (ids: string[]) => void
  refreshUser: () => Promise<void>
  initialize: () => void
  loadAssignedKegiatan: (userId: string) => Promise<void>
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      role: 'anggota' as UserRole,
      isAuthenticated: false,
      assignedKegiatanIds: [],
      adminCollection: '',

      initialize: () => {
        if (pb.authStore.isValid) {
          const model = pb.authStore.model as Record<string, unknown> | null
          if (model) {
            const coll = (model.collectionName as string) || ''
            let role: UserRole = 'anggota'
            if (coll === 'admin_wilayah_auth') {
              role = (model.is_super_admin === true || model.is_super_admin === 'true')
                ? 'super_admin'
                : 'stadion'
            } else if (coll === 'admin_auth') {
              role = 'stadion'
            }
            const user: ExtendedUser = {
              id: model.id as string,
              collectionId: model.collectionId as string,
              collectionName: model.collectionName as string,
              collection: model.collection as string,
              created: model.created as string,
              updated: model.updated as string,
              email: (model.email as string) || '',
              emailVisibility: (model.emailVisibility as boolean) || false,
              username: (model.username as string) || (model.nowa as string) || '',
              name: (model.nama as string) || '',
              nowa: (model.nowa as string) || '',
              telepon: (model.telepon as string) || '',
              daerah_id: (model.daerah_admin as string) || (model.daerah_id as string) || '',
              daerah_admin: (model.daerah_admin as string) || '',
              is_super_admin: (model.is_super_admin as boolean) || false,
              role,
              assignedKegiatanIds: [],
              isSuperAdmin: role === 'super_admin',
              isStadion: role === 'stadion',
              isAnggota: role === 'anggota',
            } as unknown as ExtendedUser
            set({
              user,
              role,
              isAuthenticated: true,
              adminCollection: coll,
            })
            get().loadAssignedKegiatan(model.id as string)
          }
        }
      },

      login: async (noWa: string, password: string) => {
        let normalized = noWa.replace(/\D/g, '')
        if (normalized.startsWith('62')) {
          normalized = '0' + normalized.slice(2)
        }
        if (!normalized.startsWith('0')) {
          normalized = '0' + normalized
        }
        const email = `${normalized}@iswarajatim.com`

        let authData = null
        let lastError = null
        try {
          authData = await pb.collection('admin_wilayah_auth').authWithPassword(email, password)
        } catch (err) {
          const e = err as { response?: { message?: string } }
          const msg = e?.response?.message || ''
          if (!msg.toLowerCase().includes('failed to authenticate')) {
            lastError = err
            try {
              authData = await pb.collection('admin_auth').authWithPassword(email, password)
              lastError = null
            } catch (err2) {
              lastError = err2
            }
          } else {
            lastError = err
          }
        }

        if (!authData) throw lastError

        const model = authData.record as Record<string, unknown>
        const coll = (model.collectionName as string) || ''
        let role: UserRole = 'anggota'
        if (coll === 'admin_wilayah_auth') {
          role = (model.is_super_admin === true || model.is_super_admin === 'true')
            ? 'super_admin'
            : 'stadion'
        } else if (coll === 'admin_auth') {
          role = 'stadion'
        }

        const user: ExtendedUser = {
          id: model.id as string,
          collectionId: model.collectionId as string,
          collectionName: model.collectionName as string,
          collection: model.collection as string,
          created: model.created as string,
          updated: model.updated as string,
          email: (model.email as string) || '',
          emailVisibility: (model.emailVisibility as boolean) || false,
          username: (model.username as string) || (model.nowa as string) || '',
          name: (model.nama as string) || '',
          nowa: (model.nowa as string) || '',
          telepon: (model.telepon as string) || '',
          daerah_id: (model.daerah_admin as string) || (model.daerah_id as string) || '',
          daerah_admin: (model.daerah_admin as string) || '',
          is_super_admin: (model.is_super_admin as boolean) || false,
          role,
          assignedKegiatanIds: [],
          isSuperAdmin: role === 'super_admin',
          isStadion: role === 'stadion',
          isAnggota: role === 'anggota',
        } as unknown as ExtendedUser

        set({
          user,
          role,
          isAuthenticated: true,
          assignedKegiatanIds: [],
          adminCollection: coll,
        })
        await get().loadAssignedKegiatan(model.id as string)
      },

      logout: () => {
        pb.authStore.clear()
        set({
          user: null,
          role: 'anggota',
          isAuthenticated: false,
          assignedKegiatanIds: [],
          adminCollection: '',
        })
      },

      setAssignedKegiatan: (ids: string[]) => {
        set({ assignedKegiatanIds: ids })
      },

      loadAssignedKegiatan: async (userId: string) => {
        try {
          const result = await pb.collection('admin_kegiatan').getFullList({
            filter: `user_id = "${userId}"`,
          })
          const ids = result.map((r) => r.kegiatan_id)
          set({ assignedKegiatanIds: ids })
          if (get().user) {
            set({
              user: { ...get().user!, assignedKegiatanIds: ids },
            })
          }
        } catch {
          set({ assignedKegiatanIds: [] })
        }
      },

      refreshUser: async () => {
        if (!pb.authStore.isValid) return
        try {
          const model = await pb.collection('admin_wilayah_auth').authRefresh()
          const rec = model.record as Record<string, unknown>
          const coll = (rec.collectionName as string) || ''
          let role: UserRole = 'anggota'
          if (coll === 'admin_wilayah_auth') {
            role = (rec.is_super_admin === true || rec.is_super_admin === 'true')
              ? 'super_admin'
              : 'stadion'
          } else if (coll === 'admin_auth') {
            role = 'stadion'
          }
          const user: ExtendedUser = {
            id: rec.id as string,
            collectionId: rec.collectionId as string,
            collectionName: rec.collectionName as string,
            collection: rec.collection as string,
            created: rec.created as string,
            updated: rec.updated as string,
            email: (rec.email as string) || '',
            emailVisibility: (rec.emailVisibility as boolean) || false,
            username: (rec.username as string) || (rec.nowa as string) || '',
            name: (rec.nama as string) || '',
            nowa: (rec.nowa as string) || '',
            telepon: (rec.telepon as string) || '',
            daerah_id: (rec.daerah_admin as string) || (rec.daerah_id as string) || '',
            daerah_admin: (rec.daerah_admin as string) || '',
            is_super_admin: (rec.is_super_admin as boolean) || false,
            role,
            assignedKegiatanIds: get().assignedKegiatanIds,
            isSuperAdmin: role === 'super_admin',
            isStadion: role === 'stadion',
            isAnggota: role === 'anggota',
          } as unknown as ExtendedUser
          set({ user, role, isAuthenticated: true, adminCollection: coll })
        } catch {
          get().logout()
        }
      },
    }),
    {
      name: 'iswara-auth',
      partialize: (state) => ({
        assignedKegiatanIds: state.assignedKegiatanIds,
      }),
    }
  )
)
